import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAdmin } from '@/contexts/AdminContext';
import { DEFAULT_ADMIN_PASSWORD } from '@/lib/store';

export function AdminLogin() {
  const { login } = useAdmin();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');
    const ok = await login(password);
    setLoading(false);
    if (ok) {
      navigate('/admin');
    } else {
      setError('Incorrect password. Please try again.');
    }
  }

  return (
    <div className="min-h-screen bg-[hsl(25,20%,8%)] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative mb-4">
            <div className="absolute inset-0 rounded-full bg-amber-600/20 blur-xl" />
            <img
              src="/images/DHO.jpg"
              alt="DHO"
              className="relative h-20 w-20 rounded-full object-cover ring-2 ring-amber-700/60"
            />
          </div>
          <h1 className="text-white font-bold text-xl tracking-wide">DAKOTA HORIZON</h1>
          <p className="text-amber-500 text-xs tracking-[0.2em] mt-0.5">OUTDOORS — ADMIN</p>
        </div>

        {/* Card */}
        <div className="bg-[hsl(25,20%,12%)] border border-white/10 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-6">
            <Shield className="h-5 w-5 text-amber-500" />
            <h2 className="text-white font-semibold">Admin Access</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-stone-300 text-sm">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-500" />
                <Input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="pl-9 pr-10 bg-[hsl(25,20%,8%)] border-white/10 text-white placeholder:text-stone-600 focus:border-amber-600"
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300 transition-colors"
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-red-400 text-sm flex items-center gap-1.5">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-red-400" />
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading || !password}
              className="w-full bg-amber-600 hover:bg-amber-500 text-white font-semibold h-10 rounded-sm"
            >
              {loading ? 'Verifying…' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="text-stone-600 text-xs text-center">
              Default password: <code className="text-stone-400 bg-white/5 px-1 rounded">{DEFAULT_ADMIN_PASSWORD}</code>
              <br />
              Change it in Admin → Settings after first login.
            </p>
          </div>
        </div>

        <p className="text-center mt-6">
          <a href="/" className="text-stone-500 hover:text-amber-400 text-sm transition-colors">
            ← Back to Store
          </a>
        </p>
      </div>
    </div>
  );
}

export default AdminLogin;
